/*
 * Pestaña Resumen: KPIs, valor por año, pendiente por gerencia (dona), por tipo (dona),
 * top clientes importación y antigüedad de cartera (sin importación).
 */
import { useMemo } from "react";
import { Box, Description, PieCharts, SimpleHorizontalChart, Stack, StackRow, TableBasic } from "codekit";

import Kpicards from "./Kpicards";
import Panelbox from "./Panelbox";
import Semcards from "./Semcards";
import Stakbars from "./Stakbars";
import Yearcard from "./Yearcard";
import { bucketAgingGeneral, fmtCOP, fmtMillions, groupSum, pieQuads, toMillions } from "./utils/carthelp";

const IMP = "IMPORTACION";
const AGING_KEYS = ["GERENCIA", "NOMBRE", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"];

/**
 * Ordena un mapa de sumas en filas para SimpleHorizontalChart (escala en millones)
 * @param {Object} mMap Mapa clave → valor
 * @param {number} nTop Máximo de filas
 * @returns {Array} Filas [{ name, value, sideLabel }]
 */
const toHBars = (mMap, nTop) =>
  Object.entries(mMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, nTop)
    .map(([sLabel, nValue]) => ({ name: sLabel, value: toMillions(nValue), sideLabel: fmtMillions(nValue) }));

/**
 * Ordena un mapa de sumas en datos y colores para la dona PieCharts
 * @param {Object} mMap Mapa clave → valor
 * @returns {Object} { data, colors, total }
 */
const toPie = (mMap) => {
  const mEntries = Object.entries(mMap).sort((a, b) => b[1] - a[1]);
  return {
    data: mEntries.map(([sName, nValue]) => ({ name: sName, value: nValue })),
    colors: mEntries.map((_e, nIndex) => pieQuads[nIndex % pieQuads.length]),
    total: mEntries.reduce((a, e) => a + e[1], 0),
  };
};

/**
 * Construye los KPIs de la vista
 * @param {Array} mData Datos de trabajo
 * @returns {Array} KPIs [{ label, value, delta }]
 */
const buildKpis = (mData) => {
  const nTotal = mData.reduce((a, r) => a + r.VALOR, 0);
  const mByCliente = groupSum(mData, "NOMBRE");
  const mTop = Object.entries(mByCliente).sort((a, b) => b[1] - a[1])[0];
  return [
    { label: "Valor Total Pendiente", value: fmtCOP(nTotal), delta: "" },
    { label: "DO Pendientes", value: mData.length.toLocaleString("es-CO"), delta: "" },
    {
      label: "Cliente Mayor Exposición",
      value: mTop ? mTop[0].substring(0, 26) : "-",
      delta: mTop ? fmtCOP(mTop[1]) : "",
    },
  ];
};

/**
 * Agrupa las filas de antigüedad en rangos y por cliente
 * @param {Array} mRows Filas sin importación con antigüedad válida
 * @returns {Object} { groups, byClient, critical }
 */
const buildAging = (mRows) => {
  const mGroups = {
    verde: { count: 0, valor: 0, rows: [] },
    amarillo: { count: 0, valor: 0, rows: [] },
    rojo: { count: 0, valor: 0, rows: [] },
  };
  const mByClient = {};
  mRows.forEach((r) => {
    const sB = bucketAgingGeneral(r._dias);
    mGroups[sB].count++;
    mGroups[sB].valor += r.VALOR;
    mGroups[sB].rows.push(r);
    if (!mByClient[r.NOMBRE]) mByClient[r.NOMBRE] = { verde: 0, amarillo: 0, rojo: 0, total: 0 };
    mByClient[r.NOMBRE][sB] += r.VALOR;
    mByClient[r.NOMBRE].total += r.VALOR;
  });
  const mByClientTop = Object.entries(mByClient)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 15)
    .map(([label, v]) => ({ label, ...v }));
  const mCritical = mRows
    .filter((r) => bucketAgingGeneral(r._dias) === "rojo")
    .sort((a, b) => b._dias - a._dias)
    .slice(0, 30);
  return { groups: mGroups, byClient: mByClientTop, critical: mCritical };
};

//Columnas de la tabla de DOs críticos (ids sin espacios: se usan como grid-area)
const criticalColumns = [
  { id: "DONROXXX", title: "DO", sorted: false, orderBy: null, center: false },
  { id: "CLIENTEX", title: "Cliente", sorted: false, orderBy: null, center: false },
  { id: "GERENCIA", title: "Gerencia", sorted: false, orderBy: null, center: true },
  { id: "TIPOOPEX", title: "Tipo", sorted: false, orderBy: null, center: false },
  { id: "DIASXXXX", title: "Días", sorted: false, orderBy: null, center: true },
  { id: "VALORXXX", title: "Valor", sorted: false, orderBy: null, center: false },
];

/**
 * Renderiza la pestaña Resumen
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Datos de trabajo filtrados
 * @param {Function} props.onFunnel Abre el drill-down (scope, keys, breadcrumb)
 */
const Tabresum = ({ data, onFunnel }) => {
  const mKpis = useMemo(() => buildKpis(data), [data]);
  const mYears = useMemo(() => {
    const mBy = {};
    data.forEach((r) => {
      const sY = r.ANIO || "S/A";
      if (!mBy[sY]) mBy[sY] = { valor: 0, count: 0 };
      mBy[sY].valor += r.VALOR;
      mBy[sY].count++;
    });
    return Object.keys(mBy)
      .sort((a, b) => (a === "S/A" ? 9999 : +a) - (b === "S/A" ? 9999 : +b))
      .map((sY) => ({ year: sY, valor: mBy[sY].valor, count: mBy[sY].count }));
  }, [data]);
  const mGerencia = useMemo(() => toPie(groupSum(data, "GERENCIA")), [data]);
  const mTipo = useMemo(() => toPie(groupSum(data, "TIPO DE OPERACIÓN")), [data]);
  const mCliImp = useMemo(
    () => toHBars(groupSum(data.filter((r) => r["TIPO DE OPERACIÓN"] === IMP), "NOMBRE"), 15),
    [data],
  );
  const mAging = useMemo(
    () => buildAging(data.filter((r) => r["TIPO DE OPERACIÓN"] !== IMP && r._dias !== null)),
    [data],
  );
  const mCriticalRows = useMemo(
    () =>
      mAging.critical.map((mRow, nIndex) => ({
        id: nIndex,
        DONROXXX: mRow.DO,
        CLIENTEX: mRow.NOMBRE,
        GERENCIA: mRow.GERENCIA,
        TIPOOPEX: mRow["TIPO DE OPERACIÓN"],
        DIASXXXX: mRow._dias,
        VALORXXX: fmtCOP(mRow.VALOR),
      })),
    [mAging],
  );

  //Abre el año seleccionado en el drill-down
  const openYear = (sYear) => {
    const mScope = data.filter((r) => String(r.ANIO ?? "S/A") === String(sYear));
    onFunnel(mScope, AGING_KEYS, [`Año ${sYear}`]);
  };

  return (
    <>
      <Box mb={5}>
        <Kpicards kpis={mKpis} />
      </Box>
      <Panelbox title="Valor Pendiente por Año del DO" hint="Click en un año → Gerencia → Cliente → Tipo → Director → detalle de DOs">
        <Yearcard years={mYears} onYearClick={openYear} />
      </Panelbox>
      <Panelbox title="Pendiente por Gerencia" hint="Click en un segmento → Cliente → Tipo → Director → detalle de DOs">
        <Stack gap={8}>
          <StackRow>
            <PieCharts
              data={mGerencia.data}
              chartColors={mGerencia.colors}
              labelOne={fmtCOP(mGerencia.total)}
              labelTwo="Pendiente"
              isCurrency
              onClickCellChart={(c) => onFunnel(data.filter((r) => r.GERENCIA === c.data.name), ["NOMBRE", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"], [`Gerencia ${c.data.name}`])}
              onClickCard={(c) => onFunnel(data.filter((r) => r.GERENCIA === c.data.name), ["NOMBRE", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"], [`Gerencia ${c.data.name}`])}
            />
          </StackRow>
        </Stack>
      </Panelbox>
      <Panelbox title="Por Tipo de Operación" hint="Click en un segmento → Director → detalle de DOs">
        <Stack gap={8}>
          <StackRow>
            <PieCharts
              data={mTipo.data}
              chartColors={mTipo.colors}
              labelOne={fmtCOP(mTipo.total)}
              labelTwo="Pendiente"
              isCurrency
              onClickCellChart={(c) => onFunnel(data.filter((r) => r["TIPO DE OPERACIÓN"] === c.data.name), ["DIRECTOR DE CUENTA"], [`Tipo ${c.data.name}`])}
              onClickCard={(c) => onFunnel(data.filter((r) => r["TIPO DE OPERACIÓN"] === c.data.name), ["DIRECTOR DE CUENTA"], [`Tipo ${c.data.name}`])}
            />
          </StackRow>
        </Stack>
      </Panelbox>
      <Panelbox title="Top Clientes con Mayor Exposición en Importación" hint="De mayor a menor — quién concentra el riesgo de importaciones">
        <SimpleHorizontalChart
          subtitle="Cifras en millones de COP"
          barColor="primary-600"
          maxItems={8}
          data={mCliImp}
        />
      </Panelbox>
      <Panelbox title="Antigüedad de Cartera (sin Importación)" hint="Corte: 0-30 días · 31-60 días · +60 días desde creación · cifras en millones de COP">
        <Box mb={5}>
          <Semcards
            groups={mAging.groups}
            onCardClick={(sK) => onFunnel(mAging.groups[sK].rows, AGING_KEYS, [`Antigüedad · ${sK}`])}
          />
        </Box>
        <Stakbars
          data={mAging.byClient}
          onItemClick={(m) =>
            onFunnel(
              data.filter((r) => r["TIPO DE OPERACIÓN"] !== IMP && r._dias !== null && r.NOMBRE === m.label),
              ["GERENCIA", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"],
              [`Antigüedad · Cliente ${m.label}`],
            )
          }
        />
        <Box mt={5} mb={2}>
          <Description variant="description-3" color="neutral-500">
            DOs más críticos (mayor antigüedad) — click en una fila para ver el detalle del cliente
          </Description>
        </Box>
        <TableBasic
          columns={criticalColumns}
          data={mCriticalRows}
          dataLength={mCriticalRows.length}
          hasMore={false}
          fetchData={() => {}}
          onRowClick={({ row }) =>
            onFunnel(
              data.filter((x) => x.NOMBRE === row.CLIENTEX && x["TIPO DE OPERACIÓN"] !== IMP),
              ["GERENCIA", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"],
              [`Cliente ${row.CLIENTEX}`],
            )
          }
        />
      </Panelbox>
    </>
  );
};

export default Tabresum;
